import { useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { DEFAULT_TAB, isSettingsTab, type SettingsTab } from './collections';

/**
 * Which settings tab is open, in the URL.
 *
 * ⭐ "Have a look at the transition matrix" is a link somebody sends. The rest of this app already
 * puts the backlog's filters, the board's lane and the versions tab in the query string for exactly
 * that reason.
 *
 * ⚠️ `replace` — switching tab is a change of VIEW, not of place. Pushing would make Back walk
 * through seven tabs instead of leaving the settings screen.
 *
 * ⛔ An unrecognised `?tab=` falls back to the default rather than rendering nothing. A stale
 * bookmark is not an error state.
 */
export function useSettingsTab(): [SettingsTab, (tab: SettingsTab) => void] {
  const [params, setParams] = useSearchParams();
  const raw = params.get('tab');
  const tab: SettingsTab = isSettingsTab(raw) ? raw : DEFAULT_TAB;

  const setTab = useCallback(
    (next: SettingsTab) => {
      const search = new URLSearchParams(params);
      if (next === DEFAULT_TAB) search.delete('tab');
      else search.set('tab', next);
      setParams(search, { replace: true });
    },
    [params, setParams],
  );

  return [tab, setTab];
}
